import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, ChapterLabel } from "./Reveal";
import { DEVICES } from "@/data";

function SelectedViewer({ device }) {
  const [rot, setRot] = useState({ x: 0, y: 0 });
  const [activeHotspot, setActiveHotspot] = useState(null);
  const dragState = useRef(null);

  const onPointerDown = (e) => {
    dragState.current = { startX: e.clientX, startY: e.clientY, baseY: rot.y, baseX: rot.x };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    setRot({
      y: Math.max(-24, Math.min(24, dragState.current.baseY + (e.clientX - dragState.current.startX) / 9)),
      x: Math.max(-10, Math.min(10, dragState.current.baseX - (e.clientY - dragState.current.startY) / 14)),
    });
  };
  const onPointerUp = () => (dragState.current = null);

  return (
    <div
      data-testid={`device-viewer-${device.id}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{ touchAction: "pan-y", perspective: 1000 }}
      className="relative w-fit cursor-grab active:cursor-grabbing select-none"
    >
      <motion.img
        src={device.img}
        alt={device.name}
        draggable={false}
        animate={{ rotateY: rot.y, rotateX: rot.x }}
        transition={{ type: "spring", stiffness: 120, damping: 18 }}
        className={`blend-screen w-auto object-contain ${
          device.id === "ring" ? "h-40 sm:h-48" : "h-44 sm:h-56"
        }`}
      />
      {device.hotspots?.map((h, i) => (
        <button
          key={h.label}
          data-testid={`hotspot-${device.id}-${i}`}
          onClick={() => setActiveHotspot(activeHotspot === i ? null : i)}
          onMouseEnter={() => setActiveHotspot(i)}
          style={{ left: `${h.x}%`, top: `${h.y}%` }}
          className="aura-hotspot absolute z-10 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#38BDF8] cursor-pointer"
          aria-label={h.label}
        />
      ))}
      <AnimatePresence>
        {activeHotspot !== null && device.hotspots && (
          <motion.div
            key={activeHotspot}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.3 }}
            data-testid="hotspot-tooltip"
            className="absolute -bottom-2 left-0 w-max max-w-[220px] rounded-lg border border-white/10 bg-[#0B1120]/95 px-4 py-3 backdrop-blur-md pointer-events-none z-20"
          >
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#38BDF8]">
              {device.hotspots[activeHotspot].label}
            </p>
            <p className="mt-1 text-xs text-slate-300 leading-relaxed">
              {device.hotspots[activeHotspot].desc}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Devices() {
  const [selectedId, setSelectedId] = useState("teacher");
  const device = DEVICES.find((d) => d.id === selectedId);

  return (
    <section id="devices" data-testid="devices-section" className="relative py-16 sm:py-24 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <ChapterLabel number="05" label="The Devices" />
        <Reveal delay={0.1}>
          <h2
            data-testid="devices-heading"
            className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#FFFDF9]"
          >
            Three devices.
            <br />
            One learning <span className="italic text-[#38BDF8]">experience</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-4 max-w-md text-sm text-slate-400 leading-relaxed">
            Each device has a role in connecting the classroom experience.
          </p>
          <p data-testid="devices-instruction" className="mt-6 text-[10px] tracking-[0.35em] uppercase text-[#38BDF8]/80">
            Click a device to explore
          </p>
        </Reveal>

        <div className="mt-10 grid items-center gap-10 lg:grid-cols-2">
          <div className="flex flex-col gap-7">
            {DEVICES.map((d) =>
              d.id === selectedId ? (
                <div key={d.id} data-testid={`device-selected-${d.id}`} className="flex flex-col gap-3">
                  <SelectedViewer device={d} />
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#FFFDF9]">{d.name}</span>
                    <span className="h-px w-10 bg-[#38BDF8]" />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500">Drag to rotate</span>
                  </div>
                </div>
              ) : (
                <button
                  key={d.id}
                  data-testid={`device-thumb-${d.id}`}
                  onClick={() => setSelectedId(d.id)}
                  className="group flex items-center gap-5 opacity-45 hover:opacity-85 transition-opacity duration-500 text-left"
                >
                  <img
                    src={d.img}
                    alt={d.name}
                    draggable={false}
                    className={`blend-screen w-auto object-contain transition-transform duration-500 group-hover:-translate-y-1 ${
                      d.id === "ring" ? "h-16" : "h-20"
                    }`}
                  />
                  <div>
                    <p className="text-sm text-slate-200">{d.name}</p>
                    <p className="mt-1 text-[10px] tracking-[0.25em] uppercase text-slate-500 group-hover:text-[#38BDF8] transition-colors">
                      Click to explore
                    </p>
                  </div>
                </button>
              )
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={device.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`device-panel-${device.id}`}
            >
              <p className="text-[10px] tracking-[0.35em] uppercase text-slate-500">Concept hardware</p>
              <h3 data-testid="device-name" className="mt-3 font-display text-2xl sm:text-3xl text-[#FFFDF9]">
                {device.name}
              </h3>
              <p className="mt-1.5 font-display italic text-base text-[#38BDF8]/90">{device.tagline}</p>
              <p data-testid="device-desc" className="mt-4 max-w-md text-sm text-slate-300 leading-relaxed">
                {device.desc}
              </p>

              {device.hotspots && (
                <ul className="mt-6 space-y-2.5">
                  {[...device.hotspots, ...(device.extras || [])].map((h) => (
                    <li key={h.label} className="flex items-baseline gap-3 text-sm">
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#38BDF8]" />
                      <span className="w-32 shrink-0 text-slate-200">{h.label}</span>
                      <span className="text-slate-400">{h.desc}</span>
                    </li>
                  ))}
                </ul>
              )}

              {device.interactions && (
                <>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {device.interactions.map((a) => (
                      <span
                        key={a.label}
                        data-testid={`ring-action-${a.label.toLowerCase()}`}
                        title={a.desc}
                        className="rounded-full border border-[#38BDF8]/30 px-4 py-1.5 text-[11px] tracking-[0.15em] uppercase text-[#38BDF8] hover:bg-[#38BDF8]/10 transition-colors duration-300 cursor-default"
                      >
                        {a.label}
                      </span>
                    ))}
                  </div>
                  <p data-testid="ring-no-camera-note" className="mt-5 text-xs text-slate-500 tracking-wide">
                    {device.note}
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
